import { parseSpreadsheetNumber } from './rosterHelpers';

const EMPTY_FORWARD_LINES = () =>
  Array.from({ length: 5 }, () => ({ LW: null, C: null, RW: null }));

const EMPTY_DEFENSE_LINES = () =>
  Array.from({ length: 4 }, () => ({ D1: null, D2: null }));

const EMPTY_GOALIE_SLOTS = () => [null, null, null];

function sortByOvDesc(a, b) {
  return (b.OV || 0) - (a.OV || 0);
}

/**
 * Player is on a team roster: column D matches and column AF is at least 1.
 */
export function isRosteredOnTeam(player, teamName) {
  if (!player || !teamName || teamName === 'Free Agents' || teamName === 'N/A') {
    return false;
  }

  if (player.team !== teamName) return false;

  if (player.contractAf !== undefined) {
    return parseSpreadsheetNumber(player.contractAf) >= 1;
  }

  if (player.isFreeAgent !== undefined) {
    return !player.isFreeAgent;
  }

  return true;
}

/**
 * Goalie is on a team roster: column D matches and column Z is at least 1.
 */
export function isRosteredGoalieOnTeam(goalie, teamName) {
  if (!goalie || !teamName || teamName === 'Free Agents' || teamName === 'N/A') {
    return false;
  }

  if (goalie.team !== teamName) return false;

  if (goalie.contractZ !== undefined) {
    return parseSpreadsheetNumber(goalie.contractZ) >= 1;
  }

  if (goalie.isFreeAgent !== undefined) {
    return !goalie.isFreeAgent;
  }

  return true;
}

function assignToSlots(sortedPool, lines, slotKey, usedIds) {
  let poolIdx = 0;
  for (let i = 0; i < lines.length; i += 1) {
    while (poolIdx < sortedPool.length && usedIds.has(sortedPool[poolIdx].id)) {
      poolIdx += 1;
    }
    if (poolIdx >= sortedPool.length) break;
    lines[i][slotKey] = sortedPool[poolIdx];
    usedIds.add(sortedPool[poolIdx].id);
    poolIdx += 1;
  }
}

function assignDefensePairs(sortedDefense, lines, usedIds) {
  let poolIdx = 0;
  for (let i = 0; i < lines.length; i += 1) {
    for (const slot of ['D1', 'D2']) {
      while (poolIdx < sortedDefense.length && usedIds.has(sortedDefense[poolIdx].id)) {
        poolIdx += 1;
      }
      if (poolIdx >= sortedDefense.length) return;
      lines[i][slot] = sortedDefense[poolIdx];
      usedIds.add(sortedDefense[poolIdx].id);
      poolIdx += 1;
    }
  }
}

function collectSalaries(...groups) {
  const salaries = {};
  groups.flat().forEach((player) => {
    if (player?.id != null) {
      salaries[player.id] = String(player.salary || 0);
    }
  });
  return salaries;
}

/**
 * Build a lineup from rostered skaters (AF >= 1) and goalies (Z >= 1) on the selected team.
 * Fills slots by overall rating (OV), highest first.
 */
export function buildLineupFromTeam(players, goalies, teamName) {
  const rosterSkaters = players.filter((p) => isRosteredOnTeam(p, teamName));
  const rosterGoalies = goalies.filter((g) => isRosteredGoalieOnTeam(g, teamName));
  const usedIds = new Set();

  const forwards = EMPTY_FORWARD_LINES();
  const defensemen = EMPTY_DEFENSE_LINES();
  const lineupGoalies = EMPTY_GOALIE_SLOTS();

  const canPlay = (player, code) => player.positions?.includes(code);

  assignToSlots(
    rosterSkaters.filter((p) => canPlay(p, 'C')).sort(sortByOvDesc),
    forwards,
    'C',
    usedIds
  );
  assignToSlots(
    rosterSkaters.filter((p) => canPlay(p, 'L')).sort(sortByOvDesc),
    forwards,
    'LW',
    usedIds
  );
  assignToSlots(
    rosterSkaters.filter((p) => canPlay(p, 'R')).sort(sortByOvDesc),
    forwards,
    'RW',
    usedIds
  );
  assignDefensePairs(
    rosterSkaters.filter((p) => canPlay(p, 'D')).sort(sortByOvDesc),
    defensemen,
    usedIds
  );

  rosterGoalies.sort(sortByOvDesc).slice(0, 3).forEach((goalie, index) => {
    lineupGoalies[index] = goalie;
  });

  const salaries = collectSalaries(
    forwards.flatMap((line) => Object.values(line)),
    defensemen.flatMap((line) => Object.values(line)),
    lineupGoalies
  );

  return {
    forwards,
    defensemen,
    lineupGoalies,
    salaries,
    rosterCount: rosterSkaters.length + rosterGoalies.length,
  };
}

export function getRosterTeamOptions(players, goalies) {
  const teams = new Set([
    ...players.map((p) => p.team),
    ...goalies.map((g) => g.team),
  ]);
  teams.delete('Free Agents');
  teams.delete('N/A');
  teams.delete('');
  return [...teams].sort((a, b) => a.localeCompare(b));
}

export const USER_TEAM_STORAGE_PREFIX = 'tugg_user_team_';

export function loadSavedUserTeam(userId) {
  if (!userId) return '';
  try {
    return localStorage.getItem(`${USER_TEAM_STORAGE_PREFIX}${userId}`) || '';
  } catch {
    return '';
  }
}

export function saveUserTeam(userId, teamName) {
  if (!userId) return;
  try {
    if (teamName) {
      localStorage.setItem(`${USER_TEAM_STORAGE_PREFIX}${userId}`, teamName);
    } else {
      localStorage.removeItem(`${USER_TEAM_STORAGE_PREFIX}${userId}`);
    }
  } catch {
    // ignore
  }
}
